import * as React from "react";
import { Button, Hint, Input, Link, Modal } from "react-ui";

import { Icon } from "../../../../Icons/Icon";

import {
    ActionsContainer,
    EmptyMessageContainer1,
    EmptyMessageContainer2,
    EmptyMessageHeader,
    Header,
    HeaderBottomBorder,
    HeaderPanel,
    HeaderPanelLine,
    HeaderTable,
    HeaderTableCell,
    HorizontalLoaderContainer,
    ItemTable,
    ItemTableActionsCell,
    ItemTableCell,
    ItemTableLeftPaddingCell,
    ItemTableContainer,
    ItemTableRow,
    RemovedPanel,
    RemovePanelContent,
    RestItemsMessage,
    RoundedButton,
} from "./SelectEntryModal.styles";
import { EmptyListIcon } from "./EmptyListIcon";
import { UserFriendlyLoadingState, useUserFriendlyLoadingState } from "./UserFriendlyLoadingState";
import { HorizontalLoader } from "./HorizontalLoader";

interface EntryColumn<TItem> {
    title: string;
    width: string;
    value: (item: TItem) => string | undefined;
}

interface SelectEntryModalProps<TItem> {
    searchString: string;
    onChangeSearchString: (searchString: string) => void;
    onRequestAddEntry: () => void;
    title?: React.ReactNode;
    emptyMessage?: React.ReactNode;
    showEmptyMessage: boolean;
    addButtonText?: React.ReactNode;
    columns: Array<EntryColumn<TItem>>;
    items: TItem[];
    getItemId: (item: TItem) => string;
    removedItems?: TItem[];
    onSelectItem: (item: TItem) => void;
    onRequestItemUpdate: (item: TItem) => void;
    onRequestItemDelete: (item: TItem) => void;
    onRequestRestoreItem: (item: TItem) => void;
    onClose: () => void;
    loading: boolean;
    restItemsMessage: React.ReactNode;
}

export function SelectEntryModal<TItem>(props: SelectEntryModalProps<TItem>): JSX.Element {
    const getItemKey = (i: TItem) => props.getItemId(i) ?? props.columns.map(x => x.value(i)).join("-");
    const getColumnWidth = (column: EntryColumn<TItem>) =>
        Math.round((Number(column.width.replace("%", "")) * 735) / 100);

    const loadingState = useUserFriendlyLoadingState(props.loading);

    return (
        <Modal ignoreBackgroundClick onClose={props.onClose}>
            <Modal.Header>
                <Header>{props.title ?? `Выбор из справочника`}</Header>
                <HeaderPanel>
                    <HeaderPanelLine>
                        <Link data-tid="AddEntry" icon={<Icon name={"PlusIcon"} />} onClick={props.onRequestAddEntry}>
                            {props.addButtonText ?? `Добавить новое значение`}
                        </Link>
                    </HeaderPanelLine>
                    <HeaderPanelLine>
                        <Input
                            value={props.searchString}
                            onValueChange={props.onChangeSearchString}
                            leftIcon={<Icon name={"SearchLoupeIcon"} />}
                            width={"100%"}
                        />
                    </HeaderPanelLine>
                    <HeaderTable>
                        <colgroup>
                            {props.columns.map(column => (
                                <col key={column.title} width={getColumnWidth(column)} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                {props.columns.map(column => (
                                    <HeaderTableCell key={column.title}>{column.title}</HeaderTableCell>
                                ))}
                            </tr>
                        </thead>
                    </HeaderTable>
                    <HorizontalLoaderContainer>
                        <HorizontalLoader loadingState={loadingState} />
                    </HorizontalLoaderContainer>
                    <HeaderBottomBorder />
                </HeaderPanel>
            </Modal.Header>
            <Modal.Body>
                {props.items.length === 0 && props.showEmptyMessage ? (
                    <EmptyMessageContainer1>
                        <EmptyMessageContainer2>
                            <div>
                                <EmptyListIcon />
                            </div>
                            {props.emptyMessage ?? (
                                <>
                                    <EmptyMessageHeader>Справочник пуст</EmptyMessageHeader>
                                    Справочник хранит данные, заполенные в других формах.
                                    <br />
                                    <Link onClick={props.onRequestAddEntry}>Добавьте значение</Link> для заполнения
                                    других отчетов.
                                </>
                            )}
                        </EmptyMessageContainer2>
                    </EmptyMessageContainer1>
                ) : (
                    <ItemTableContainer
                        overlay={
                            loadingState === UserFriendlyLoadingState.LoadingState2 ||
                            loadingState === UserFriendlyLoadingState.LoadingState3 ||
                            loadingState === UserFriendlyLoadingState.LoadingState4 ||
                            loadingState === UserFriendlyLoadingState.LoadingState5
                        }>
                        <ItemTable>
                            <div>
                                {props.items.map(item => (
                                    <EntryRow<TItem>
                                        item={item}
                                        loading={props.loading}
                                        key={getItemKey(item)}
                                        removed={(props.removedItems ?? []).includes(item)}
                                        columns={props.columns}
                                        getColumnWidth={getColumnWidth}
                                        onSelect={props.onSelectItem}
                                        onRequestUpdate={props.onRequestItemUpdate}
                                        onRequestDelete={props.onRequestItemDelete}
                                        onRequestRestore={props.onRequestRestoreItem}
                                    />
                                ))}
                            </div>
                        </ItemTable>
                        {props.restItemsMessage && <RestItemsMessage>{props.restItemsMessage}</RestItemsMessage>}
                    </ItemTableContainer>
                )}
            </Modal.Body>
        </Modal>
    );
}

interface EntryRowProps<TItem> {
    item: TItem;
    removed: boolean;
    loading?: boolean;
    columns: Array<EntryColumn<TItem>>;
    getColumnWidth: (column: EntryColumn<TItem>) => number;
    onSelect: (item: TItem) => void;
    onRequestUpdate: (item: TItem) => void;
    onRequestDelete: (item: TItem) => void;
    onRequestRestore: (item: TItem) => void;
}

function EntryRow<TItem>(props: EntryRowProps<TItem>): JSX.Element {
    return (
        <ItemTableRow onClick={() => props.onSelect(props.item)} removed={props.removed}>
            <ItemTableLeftPaddingCell />
            {props.columns.map(column => (
                <ItemTableCell width={props.getColumnWidth(column)} key={column.title}>
                    {column.value(props.item)}
                </ItemTableCell>
            ))}
            <ItemTableActionsCell key="RightPadding">
                <RemovedPanel active={props.removed} onClick={e => e.stopPropagation()}>
                    <RemovePanelContent>
                        Запись удалена.{" "}
                        <Button
                            use="link"
                            active={props.loading}
                            disabled={props.loading}
                            onClick={e => {
                                e.stopPropagation();
                                props.onRequestRestore(props.item);
                            }}>
                            Восстановить
                        </Button>
                    </RemovePanelContent>
                </RemovedPanel>
                {!props.removed && !props.loading && (
                    <ActionsContainer>
                        <Hint text="Редактировать">
                            <RoundedButton
                                onClick={e => {
                                    e.stopPropagation();
                                    props.onRequestUpdate(props.item);
                                }}>
                                <Icon name="ToolPencilLineIcon" size={20} />
                            </RoundedButton>
                        </Hint>
                        <Hint text="Удалить">
                            <RoundedButton
                                onClick={e => {
                                    e.stopPropagation();
                                    props.onRequestDelete(props.item);
                                }}>
                                <Icon name="XIcon" size={20} />
                            </RoundedButton>
                        </Hint>
                    </ActionsContainer>
                )}
            </ItemTableActionsCell>
        </ItemTableRow>
    );
}
